import type { UserSkill } from '@compass/client/types/user';
import { Award, Crown } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { skillLabel, SkillRole, skillRole, skillRoleLabel } from '@/utils/skills';

interface UserSkillBadgeProps {
  skill: UserSkill;
}

/**
 * Read-only badge rendering a skill as "<icon> skill_name · Role".
 * Backup/Primary use an award icon; the single leader for the skill is
 * highlighted with a crown icon and a primary accent so it stands out.
 */
export const UserSkillBadge: React.FC<UserSkillBadgeProps> = ({ skill }) => {
  const role = skillRole(skill);
  const isLeader = role === SkillRole.Leader;
  const Icon = isLeader ? Crown : Award;

  return (
    <Badge
      variant="outline"
      className={cn('gap-1 whitespace-nowrap', isLeader && 'border-primary text-primary')}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span>{skillLabel(skill.skill)}</span>
      <span className={cn(!isLeader && 'opacity-70')}>· {skillRoleLabel(role)}</span>
    </Badge>
  );
};
